// databaseAboutUtils.js

import { getDatabaseTypeLabelFormatError } from 'utilities/databaseTypeLabels';
import { pluralizeTitle } from 'utilities/Languages';
import { ViewType } from 'utilities/NodeType';
import { toLowerSnakeCase } from 'utilities/Strings';
import { typeAllowsTopLevelCreate } from 'utilities/typeCreatePolicy';
import {
  typeShowsInAddMenu,
  typeShowsInMenu,
} from 'utilities/typeDisplayPolicy';
import {
  getDefaultAppearAsRows,
  getTypeAppearAsDraftRows,
  getTypeAppearAsRows,
} from 'utilities/typeAppearPolicy';

const normalizeString = (value) => String(value || '').trim();

/**
 * Coerces About draft fields for live editing (does not trim label/description,
 * so spaces can be typed between words).
 *
 * @param {object} [draft]
 * @returns {object}
 */
export function normalizeDatabaseAboutDraft(draft = {}) {
  const label = String(draft?.label ?? '');
  const labelPlural = String(draft?.labelPlural ?? '');
  const iconKey = toLowerSnakeCase(normalizeString(draft?.iconKey) || 'deployed_code') || 'deployed_code';

  return {
    defaultView: normalizeString(draft?.defaultView || ViewType.LIST).toUpperCase() || ViewType.LIST,
    description: String(draft?.description ?? ''),
    iconKey,
    label,
    labelPlural: labelPlural || (normalizeString(label) ? pluralizeTitle(normalizeString(label)) : ''),
    showPeekOnOpen: draft?.showPeekOnOpen !== false,
    allowTopLevelCreate: typeAllowsTopLevelCreate(draft),
    showInMenu: typeShowsInMenu(draft),
    showInAddMenu: typeShowsInAddMenu(draft),
    appearAs: Array.isArray(draft?.appearAs)
      ? getTypeAppearAsDraftRows(draft)
      : getDefaultAppearAsRows(draft),
  };
}

/**
 * Normalizes About draft for persistence (trims strings).
 *
 * @param {object} [draft]
 * @returns {object}
 */
export function normalizeDatabaseAboutDraftForSave(draft = {}) {
  const label = normalizeString(draft?.label);
  const labelPlural = normalizeString(draft?.labelPlural);
  const iconKey = toLowerSnakeCase(normalizeString(draft?.iconKey) || 'deployed_code') || 'deployed_code';

  return {
    defaultView: normalizeString(draft?.defaultView || ViewType.LIST).toUpperCase() || ViewType.LIST,
    description: normalizeString(draft?.description),
    iconKey,
    label,
    labelPlural: labelPlural || (label ? pluralizeTitle(label) : ''),
    showPeekOnOpen: draft?.showPeekOnOpen !== false,
    allowTopLevelCreate: typeAllowsTopLevelCreate(draft),
    showInMenu: typeShowsInMenu(draft),
    showInAddMenu: typeShowsInAddMenu(draft),
    appearAs: getTypeAppearAsRows(draft),
  };
}

export function isPluralManuallyTouched(label, labelPlural) {
  const cleanedLabel = normalizeString(label);
  const cleanedPlural = normalizeString(labelPlural);
  const expectedPlural = cleanedLabel ? pluralizeTitle(cleanedLabel) : '';

  return Boolean(
    cleanedPlural
    && expectedPlural
    && cleanedPlural.toLowerCase() !== expectedPlural.toLowerCase(),
  );
}

export function resolveAutoPluralLabel(label, { pluralTouched = false, currentPlural = '' } = {}) {
  if (pluralTouched) return String(currentPlural ?? '');
  const cleanedLabel = normalizeString(label);
  if (!cleanedLabel) return '';
  return pluralizeTitle(cleanedLabel);
}

export function buildDatabaseAboutDuplicateState({
  candidates = [],
  excludeKey = '',
  label = '',
  labelPlural = '',
} = {}) {
  const normalizedLabel = normalizeString(label).toLowerCase();
  const normalizedPlural = normalizeString(labelPlural).toLowerCase();
  const normalizedExcludeKey = normalizeString(excludeKey);
  const existingNames = new Set();

  (Array.isArray(candidates) ? candidates : []).forEach((candidate) => {
    if (!candidate || typeof candidate !== 'object') return;

    const candidateKey = normalizeString(candidate?.key);
    if (candidateKey && candidateKey === normalizedExcludeKey) return;

    const singular = normalizeString(candidate?.label).toLowerCase();
    const plural = normalizeString(candidate?.labelPlural).toLowerCase();

    if (singular) existingNames.add(singular);
    if (plural) existingNames.add(plural);
  });

  const labelFormatError = getDatabaseTypeLabelFormatError(label);
  const pluralFormatError = getDatabaseTypeLabelFormatError(labelPlural);

  return {
    labelError: labelFormatError
      || (normalizedLabel && existingNames.has(normalizedLabel)
        ? 'That label already exists in this base.'
        : ''),
    pluralError: pluralFormatError
      || (normalizedPlural && existingNames.has(normalizedPlural)
        ? 'That plural label already exists in this base.'
        : ''),
  };
}

export function canSaveDatabaseAboutDraft(draft, duplicateState = {}) {
  return Boolean(normalizeString(draft?.label))
    && !duplicateState.labelError
    && !duplicateState.pluralError;
}
